import { useState } from "react";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaLoadingBall from "@coworker/apprestructured/src/shared/wrappers/FixaLoading/FixaLoadingBall";
import {
  Container,
  EmptyState,
  ErrorBanner,
  ExportSelect,
  ResultCount,
  Toolbar,
  ToolbarSearch,
} from "./AtkomstUsersPage.styles";
import { CountryStorePicker } from "./CountryStorePicker";
import { UsersTable } from "./UsersTable";
import { useAtkomstUsersData } from "../hooks/useAtkomstUsersData";
import {
  AtkomstUser,
  ExportRoleType,
  SortCol,
  buildExportFileName,
  escapeCsvField,
  isCoworker,
  isCountrySU,
  isStoreSU,
} from "../utils/atkomstUsersUtils";

const AtkomstUsersPage = () => {
  const [selectedNodeRefs, setSelectedNodeRefs] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [exportRole, setExportRole] = useState<ExportRoleType>("both");

  const {
    isPlatformOwner,
    isCheckingRole,
    countryNodes,
    nodeNameByRef,
    storesByCountry,
    selectedCountryIds,
    isLoadingCountries,
    isLoadingStores,
    mergedUsers,
    isLoadingUsers,
    isFetchingUsers,
    isErrorUsers,
    displayNames,
    fullyFilteredUsers,
    countryNameById,
    storeRefToCountryId,
    paginatedUsers,
    safePage,
    totalPages,
  } = useAtkomstUsersData({
    selectedNodeRefs,
    searchQuery,
    sortCol,
    sortDir,
    page,
  });

  // ── Handlers ────────────────────────────────────────────────────────────────

  const toggleNode = (nodeRef: string) => {
    setPage(1);
    setSelectedNodeRefs((prev) =>
      prev.includes(nodeRef)
        ? prev.filter((r) => r !== nodeRef)
        : [...prev, nodeRef]
    );
  };

  const toggleAllCountries = () => {
    setPage(1);
    const allRefs = countryNodes.map((c) => c.ref);
    const allSelected = allRefs.every((r) => selectedNodeRefs.includes(r));
    setSelectedNodeRefs((prev) => {
      const without = prev.filter((r) => !allRefs.includes(r));
      return allSelected ? without : [...without, ...allRefs];
    });
  };

  const toggleAllStoresInCountry = (countryId: string, select: boolean) => {
    setPage(1);
    const refs = (storesByCountry.get(countryId) ?? []).map((s) => s.ref);
    setSelectedNodeRefs((prev) => {
      const without = prev.filter((r) => !refs.includes(r));
      return select ? [...without, ...refs] : without;
    });
  };

  const clearSelection = () => {
    setSelectedNodeRefs([]);
    setPage(1);
  };

  const cycleSort = (col: SortCol) => {
    setPage(1);
    if (sortCol !== col) {
      setSortCol(col);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortCol(null);
    }
  };

  const exportCsv = () => {
    const usersToExport = mergedUsers.filter((u) => {
      if (exportRole === "store") return isStoreSU(u);
      if (exportRole === "country") return isCountrySU(u);
      if (exportRole === "coworker") return isCoworker(u);
      if (exportRole === "coworker-and-store")
        return isCoworker(u) || isStoreSU(u);
      return isStoreSU(u) || isCountrySU(u); // "both"
    });

    const relevantRoles = (u: AtkomstUser) => {
      const parts: string[] = [];
      if (
        isStoreSU(u) &&
        (exportRole === "store" ||
          exportRole === "both" ||
          exportRole === "coworker-and-store")
      )
        parts.push("Store Super User");
      if (isCountrySU(u) && (exportRole === "country" || exportRole === "both"))
        parts.push("Country Super User");
      if (
        isCoworker(u) &&
        (exportRole === "coworker" || exportRole === "coworker-and-store")
      )
        parts.push("Coworker");
      return parts.join("; ");
    };

    const rows = usersToExport.map((u) => {
      const nodes = u.nodeRefs
        .map((ref) => {
          const nodeName = nodeNameByRef.get(ref);
          return nodeName ? `${ref} (${nodeName})` : ref;
        })
        .join("; ");
      return [
        escapeCsvField(displayNames?.[u.email] ?? ""),
        escapeCsvField(u.email),
        escapeCsvField(relevantRoles(u)),
        escapeCsvField(nodes),
      ];
    });

    const csv = [["Name", "Email", "Role", "Nodes"], ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildExportFileName(exportRole);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (isCheckingRole) {
    return (
      <Container>
        <FixaLoadingBall size="medium" />
      </Container>
    );
  }

  if (!isPlatformOwner) return null;

  return (
    <Container>
      <div>
        <h1>Atkomst Users</h1>
        <p>
          View all users configured in Atkomst for one or more stores or
          countries.
        </p>
      </div>

      <CountryStorePicker
        countryNodes={countryNodes}
        storesByCountry={storesByCountry}
        nodeNameByRef={nodeNameByRef}
        selectedNodeRefs={selectedNodeRefs}
        selectedCountryIds={selectedCountryIds}
        isLoadingCountries={isLoadingCountries}
        isLoadingStores={isLoadingStores}
        onToggleNode={toggleNode}
        onToggleAllStoresInCountry={toggleAllStoresInCountry}
        onToggleAllCountries={toggleAllCountries}
        onClearSelection={clearSelection}
      />

      {selectedNodeRefs.length === 0 ? (
        <EmptyState>
          Select one or more countries above. Stores within the selected
          countries will appear, and you can refine further by picking
          individual stores.
        </EmptyState>
      ) : (
        <>
          <Toolbar>
            <ToolbarSearch>
              <FixaSearch
                id="atkomst-users-search"
                placeholder="Search by name, email, or role"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.currentTarget.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearchQuery("");
                  setPage(1);
                }}
              />
            </ToolbarSearch>
            <ResultCount>
              {isLoadingUsers
                ? "Loading…"
                : `${fullyFilteredUsers.length} user${fullyFilteredUsers.length === 1 ? "" : "s"}`}
              {isFetchingUsers && !isLoadingUsers ? " (refreshing…)" : ""}
            </ResultCount>
            <ExportSelect
              value={exportRole}
              onChange={(e) => setExportRole(e.target.value as ExportRoleType)}
              disabled={isLoadingUsers || mergedUsers.length === 0}
              aria-label="Select role type to export"
            >
              <option value="both">Store &amp; Country Super Users</option>
              <option value="store">Store Super Users only</option>
              <option value="country">Country Super Users only</option>
              <option value="coworker">Coworkers only</option>
              <option value="coworker-and-store">
                Coworkers &amp; Store Super Users
              </option>
            </ExportSelect>
            <FixaButton
              type="secondary"
              size="small"
              disabled={isLoadingUsers || mergedUsers.length === 0}
              onClick={exportCsv}
            >
              Export CSV
            </FixaButton>
          </Toolbar>

          {isErrorUsers && (
            <ErrorBanner>
              Failed to load users for one or more selected nodes. Please try
              again.
            </ErrorBanner>
          )}

          {isLoadingUsers ? (
            <FixaLoadingBall size="medium" />
          ) : (
            <UsersTable
              paginatedUsers={paginatedUsers}
              displayNames={displayNames}
              countryNameById={countryNameById}
              storeRefToCountryId={storeRefToCountryId}
              filteredCount={fullyFilteredUsers.length}
              sortCol={sortCol}
              sortDir={sortDir}
              onCycleSort={cycleSort}
              safePage={safePage}
              totalPages={totalPages}
              onSetPage={setPage}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default AtkomstUsersPage;
